/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * RouteMap
 * --------
 * An activity's GPS route on OpenStreetMap tiles — the same react-leaflet +
 * tile.openstreetmap.org setup EventRouteMap uses (see the tile-policy TODO in
 * MapPointPicker before real traffic). The line is drawn twice, a white casing
 * under the portal orange, so it reads on any tile colour.
 *
 * `interactive` turns panning/zooming on (the detail panel); list cards stay
 * static so scrolling the feed never gets captured by a map.
 *
 * The expanded activity view adds: a `cursor`
 * point (elevation hover / replay), a `highlight` stretch (a hovered split),
 * `zoomControl={false}` with its own buttons driven through `onMap`, and a
 * refit whenever the container is resized (the drawer growing to full width).
 */

import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import FEED_TOKENS from '../../theme/feedTokens';

const ACCENT = FEED_TOKENS['--fd-accent'];
const INK = FEED_TOKENS['--fd-ink'];

export const MAP_CREDIT = 'Map © OpenStreetMap contributors';

const pin = (kind) => L.divIcon({
  className: '',
  html: `<span class="fd-pin ${kind}"></span>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});
const START = pin('start');
const END = pin('end');
const kmIcon = (k) => L.divIcon({ className: '', html: `<span class="fd-kmpin">${k}</span>`, iconSize: [20, 20], iconAnchor: [10, 10] });

function FitRoute({ positions, padding }) {
  const map = useMap();
  useEffect(() => {
    if (!map || positions.length < 2 || typeof map.fitBounds !== 'function') return;
    map.fitBounds(positions, { padding: [padding, padding] });
    if (typeof map.invalidateSize === 'function') map.invalidateSize();
  }, [map, positions, padding]);
  return null;
}

/** Hands the Leaflet map to the parent, and refits the route whenever the container resizes. */
function MapBridge({ positions, padding, onMap }) {
  const map = useMap();
  useEffect(() => { if (onMap) onMap(map); return () => { if (onMap) onMap(null); }; }, [map, onMap]);
  useEffect(() => {
    const el = map && typeof map.getContainer === 'function' ? map.getContainer() : null;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      map.invalidateSize();
      if (positions.length >= 2) map.fitBounds(positions, { padding: [padding, padding] });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [map, positions, padding]);
  return null;
}

/** Points where the route crosses each whole kilometre (thinned on long routes). */
function kmMarks(route) {
  const total = route.length ? route[route.length - 1].distM / 1000 : 0;
  const every = total > 24 ? Math.ceil(total / 12) : 1;
  const marks = [];
  let next = every;
  for (const p of route) {
    if (p.distM / 1000 >= next && next < total) {
      marks.push({ k: next, pos: [p.lat, p.lng] });
      next += every;
    }
  }
  return marks;
}

export default function RouteMap({
  route, interactive = false, showKm = false, label, onReady,
  cursor = null, highlight = null, zoomControl = interactive, onMap,
}) {
  const positions = useMemo(() => route.map((p) => [p.lat, p.lng]), [route]);
  const marks = useMemo(() => (showKm ? kmMarks(route) : []), [route, showKm]);
  if (positions.length < 2) return null;
  return (
    <div className="fd-map" role="img" aria-label={label || 'Route map'}>
      <MapContainer
        center={positions[0]}
        zoom={14}
        dragging={interactive}
        scrollWheelZoom={interactive}
        doubleClickZoom={interactive}
        touchZoom={interactive}
        keyboard={interactive}
        zoomControl={zoomControl}
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
          {...(onReady ? { eventHandlers: { load: onReady } } : {})}
        />
        <Polyline positions={positions} pathOptions={{ color: '#FFFFFF', weight: 8, opacity: 0.95, lineJoin: 'round' }} />
        <Polyline positions={positions} pathOptions={{ color: ACCENT, weight: 4, opacity: 1, lineJoin: 'round' }} />
        {highlight && highlight.length >= 2 && (
          <Polyline positions={highlight} pathOptions={{ color: INK, weight: 6, opacity: 0.9, lineJoin: 'round' }} />
        )}
        {marks.map((m) => <Marker key={m.k} position={m.pos} icon={kmIcon(m.k)} interactive={false} />)}
        <Marker position={positions[0]} icon={START} interactive={false} />
        <Marker position={positions[positions.length - 1]} icon={END} interactive={false} />
        {cursor && (
          <>
            <CircleMarker center={cursor} radius={14} interactive={false} pathOptions={{ stroke: false, fillColor: ACCENT, fillOpacity: 0.22 }} />
            <CircleMarker center={cursor} radius={7} interactive={false} pathOptions={{ color: '#FFFFFF', weight: 3, fillColor: ACCENT, fillOpacity: 1 }} />
          </>
        )}
        <FitRoute positions={positions} padding={interactive ? 28 : 22} />
        {(onMap || interactive) && <MapBridge positions={positions} padding={interactive ? 28 : 22} onMap={onMap} />}
      </MapContainer>
    </div>
  );
}
